<template>
  <div class="damage-calculator-tab">
    <div v-if="!showDamageCalculator">
      <div class="damage-calculator-tab__confirm-warning">
        <div class="damage-calculator-tab__confirm-warning-icon">
          <IEncircled />
        </div>
        <div class="damage-calculator-tab__confirm-warning-message typo-small">
          {{ $t('issues.calculation_steel_only') }}
        </div>
      </div>
    </div>
    <div
      v-else-if="viewerMode === 'viewer' && !currentImage?.calculation_type"
      class="damage-calculator-tab__type-select"
    >
      <h1>{{ $t('general.damage_calculation_type') }}</h1>
      <p>{{ $t('general.select_deformation_type') }}</p>
      <RadioButtonsEncircled
        v-model="calculationType"
        :items="actions"
        name="actions"
      />
      <button
        :class="[{
          'button': true,
          'button--disabled': !calculationType
        }]"
        type="button"
        @click="setCalculationType"
      >
        {{ $t('general.launch_damage_calculator') }}
      </button>
    </div>
    <div
      v-else-if="viewerMode === 'viewer' && currentImage?.calculation_type"
      class="damage-calculator-tab__type-edit"
    >
      <h1>{{ $t('deterioration_mechanisms.damage.name') }}</h1>
      <ul>
        <li
          v-for="description in $t(`dmr_descriptions.steel.structural_damage.${damageRating}`)"
          :key="description"
          class="typo-tiny"
        >
          {{ description }}
        </li>
      </ul>
      <div class="damage-calculator-tab__results-rating damage-calculator-tab__results-rating--transparent">
        <p class="typo-ignore">
          {{ $t('deterioration_mechanisms.structural_damage.name') }}
        </p>
        <div class="damage-calculator-tab__results-rating-box">
          <RatingCircle
            :class="[
              'icon',
              'icon--tiny',
              `${calculatedIconClass}`,
            ]"
            name="rating_circle"
          />
          <span class="damage-calculator-tab__det-mech-rating">
            {{ damageRating }}
          </span>
        </div>
      </div>
      <div class="damage-calculator-tab__type-edit-actions">
        <button
          class="button button--transparent"
          type="button"
          @click="dialogIsOpen = true"
        >
          {{ $t('general.clear') }}
        </button>
        <button
          class="button"
          type="button"
          @click="editDamageRating"
        >
          {{ $t('general.edit') }}
        </button>
      </div>
    </div>
    <div
      v-if="viewerMode === 'overall'"
      class="damage-calculator-tab__calculation"
    >
      <h1 class="typo-ignore typo-p">
        {{ $t('general.damage_calculation') }}
      </h1>
      <p>{{ $t('general.global_calculation_instruction') }}</p>
      <div class="damage-calculator-tab__element-select typo-small-title">
        <p>{{ $t('general.damage_element_select') }}</p>
        <RadioButtonsEncircled
          v-model="structure"
          :items="availableStructures"
          name="structure"
          :selectedItemValue="structure"
        />
      </div>
      <button
        v-if="!damageRating"
        :class="{
          'button': true,
          'button--disabled': typeof ratio !== 'number' || !structure,
        }"
        type="button"
        @click="calculateDamageRating"
      >
        {{ $t('general.calculate_damage') }}
      </button>
    </div>
    <div
      v-if="viewerMode === 'local'"
      class="damage-calculator-tab__calculation"
    >
      <h1 class="typo-ignore typo-p">
        {{ $t('issues.damage_calculation_heading') }}
      </h1>
      <p>{{ $t('general.local_calculation_instruction') }}</p>
      <div class="damage-calculator-tab__deviation typo-small-title">
        <div class="damage-calculator-tab__input-form-row">
          <FormRow
            :errors="deviationErrors"
            :label="$t('general.damage_deviation_length')"
            labelFor="deviation"
            :modifiers="['no-padding']"
          >
            <div class="input">
              <input
                id="deviation"
                v-model="deviation"
                type="text"
                @input="emit('resetDamageRating')"
              >
            </div>
          </FormRow>
          <div class="damage-calculator-tab__input-measure input">
            <Select
              id="deviation_unit"
              v-model="deviationUnit"
              :items="availableUnits"
              :modifiers="['flat']"
            />
          </div>
        </div>
        <div class="damage-calculator-tab__input-form-row">
          <FormRow
            :errors="thicknessErrors"
            :label="$t('general.material_thickness')"
            labelFor="thickness"
            :modifiers="['no-padding']"
          >
            <div class="input">
              <input
                id="thickness"
                v-model="thickness"
                type="text"
                @input="emit('resetDamageRating')"
              >
            </div>
          </FormRow>
          <div class="damage-calculator-tab__input-measure input">
            <Select
              id="deviation_unit"
              v-model="thicknessUnit"
              :items="availableUnits"
            />
          </div>
        </div>
      </div>
      <button
        v-if="!damageRating"
        :class="{
          'button': true,
          'button--disabled': !canCalculateLocalDamage,
        }"
        type="button"
        @click="calculateLocalDamageRating"
      >
        {{ $t('general.calculate_damage') }}
      </button>
    </div>
    <div
      v-if="damageRating && viewerMode !== 'viewer'"
      class="damage-calculator-tab__results"
    >
      <div class="damage-calculator-tab__results-heading">
        <h1 class="typo-ignore typo-p">
          {{ $t('general.result') }}
        </h1>
        <ul>
          <li
            v-for="description in $t(`dmr_descriptions.steel.structural_damage.${damageRating}`)"
            :key="description"
            class="typo-tiny"
          >
            {{ description }}
          </li>
        </ul>
      </div>
      <div class="damage-calculator-tab__results-rating">
        <p class="typo-ignore">
          {{ $t('deterioration_mechanisms.structural_damage.name') }}
        </p>
        <div class="damage-calculator-tab__results-rating-box">
          <RatingCircle
            :class="[
              'icon',
              'icon--tiny',
              `${calculatedIconClass}`,
            ]"
            name="rating_circle"
          />
          <span class="damage-calculator-tab__det-mech-rating">
            {{ damageRating }}
          </span>
        </div>
      </div>
      <template v-if="steelStructuralDamageDetmech">
        <div class="damage-calculator-tab__results-current">
          <div class="damage-calculator-tab__results-current-rating">
            <h1 class="typo-ignore typo-p">
              {{ $t('general.please_note') }}:
            </h1>
            <p class="typo-tiny">
              {{ $t('issues.result_apply_warning') }}
            </p>
          </div>
          <div
            :class="[
              'damage-calculator-tab__results-rating-box',
              'damage-calculator-tab__results-rating-box--dark'
            ]"
          >
            <RatingCircle
              :class="[
                'icon',
                'icon--tiny',
                `${conditionIconClass(steelStructuralDamageDetmech.value)}`,
              ]"
              name="rating_circle"
            />
            <span class="damage-calculator-tab__det-mech-rating">
              {{ steelStructuralDamageDetmech.value }}
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
  <Dialog
    v-if="dialogIsOpen"
    :message="$t('issues.clear_calculation_confirmation')"
    :title="$t('issues.damage_calculator')"
    @dismiss-dialog="dialogIsOpen = false"
  >
    <template #actions>
      <button
        aria-label="confirm"
        class="button button--inverse"
        type="button"
        @click="clearDamageRating"
      >
        {{ $t('general.proceed') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import I18n from 'i18n-js';
import Dialog from '@/components/generic/dialog.vue';
import RadioButtonsEncircled from '@/components/form/radio_buttons_encircled.vue';
import Select from '@/components/form/select.vue';
import FormRow from '@/components/form/row.vue';
import IEncircled from '@/images/i_encircled-16.svg';
import RatingCircle from '@/images/rating-circle-16.svg';

const emit = defineEmits([
  'calculateDamageRating',
  'calculateLocalDamageRating',
  'clearDamageRating',
  'resetDamageRating',
  'setViewerMode',
]);

const props = defineProps({
  /** The currently selected image */
  currentImage: { type: Object, required: true },
  /** The computed rating for Damage */
  damageRating: { type: Number, required: false, default: null },
  /** Structural Damage det mech for steel */
  issue: { type: Object, required: true },
  /** The currently determined ratio */
  ratio: { type: Number, required: false, default: null },
  /** Current viewer mode */
  viewerMode: { type: String, required: true },
});

const availableStructures = [
  { value: 'beam', label: I18n.t('general.beam') },
  { value: 'column', label: I18n.t('general.column') },
];

const actions = [
  {
    value: 'local',
    label: I18n.t('general.local_deformation'),
  },
  {
    value: 'overall',
    label: I18n.t('general.global_deformation'),
  },
];

const availableUnits = [
  { value: 'mm', label: I18n.t('general.mm') },
  { value: 'cm', label: I18n.t('general.cm') },
];

const calculationType = ref(null);
const structure = ref(null);
const dialogIsOpen = ref(false);
const deviation = ref(null);
const deviationUnit = ref('mm');
const thickness = ref(null);
const thicknessUnit = ref('mm');

const calculatedIconClass = computed(() => {
  return conditionIconClass(props.damageRating);
});

/** Return the Structural Damage detmech for Steel, if any */
const steelStructuralDamageDetmech = computed(() => {
  if (props.issue.element_material !== 'steel') {
    return;
  }

  return props.issue.deterioration_mechanism_ratings.find((rating) => {
    return rating['name'] === 'structural_damage';
  });
});

const showDamageCalculator = computed(() => {
  return !props.issue.element_material || props.issue.element_material === 'steel';
});

const deviationErrors = computed(() => {
  return isNaN(deviation.value) || parseFloat(deviation.value) < 0 ? [I18n.t('general.invalid_number')] : [];
});

const thicknessErrors = computed(() => {
  return isNaN(thickness.value) || parseFloat(deviation.value) < 0 ? [I18n.t('general.invalid_number')] : [];
});

const canCalculateLocalDamage = computed(() => {
  return deviation.value && thickness.value && deviationErrors.value.length === 0 && thicknessErrors.value.length === 0;
});

function conditionIconClass(rating) {
  return `issue-ratings__indicator--condition-${rating}`;
}

/**
 * Reset the damage rating (remove it from the screen) if damage rating has
 * been calculated, but the user switches the structure between beam/column
 */
watch(
  () => structure.value,
  () => {
    if (props.damageRating) {
      emit('resetDamageRating');
    }
  }
);

/**
 * If the user leaves damage calculator mode, reset the structure and calculation type
 */
watch(
  () => props.viewerMode,
  (mode) => {
    if (mode === 'viewer' && !props.currentImage?.calculation_type) {
      structure.value = null;
      calculationType.value = null;
    }
  }
);

/**
 * Reset damage rating if the local damage units are changed
 */
watch(
  () => [deviationUnit.value, thicknessUnit.value],
  () => emit('resetDamageRating')
);

function calculateDamageRating() {
  emit('calculateDamageRating', structure.value);
}

function calculateLocalDamageRating() {
  emit('calculateLocalDamageRating', {
    deviation: deviation.value,
    deviationUnit: deviationUnit.value,
    thickness: thickness.value,
    thicknessUnit: thicknessUnit.value,
  });
}

function clearDamageRating() {
  emit('clearDamageRating');
  dialogIsOpen.value = false;
}

function editDamageRating() {
  calculationType.value = props.currentImage.calculation_type;
  structure.value = props.currentImage?.damaged_structure;
  deviation.value = props.currentImage?.deviation;
  deviationUnit.value = props.currentImage?.deviation_unit;
  thickness.value = props.currentImage?.thickness;
  thicknessUnit.value = props.currentImage?.thickness_unit;
  emit('resetDamageRating');
  emit('setViewerMode', calculationType.value);
}

function setCalculationType() {
  if (calculationType.value) {
    emit('setViewerMode', calculationType.value);
  } else {
    emit('setViewerMode', 'viewer');
  }
}
</script>

<style lang="scss" scoped>
@import "@/stylesheets/global";

.damage-calculator-tab {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  &__empty-image {
    @include breakpoint(medium) {
      padding-top: bu(10);
    }

    svg {
      width: bu(9);
      height: bu(9);
    }
  }

  &__type-select {
    display: flex;
    flex-direction: column;
    padding-top: bu(1);
    width: 100%;

    h1 {
      color: map-get($foreground-color, light);
      font-size: bu(1.875);
      font-weight: 500;
      line-height: bu(2.5);
      padding-right: bu(6);
    }

    p {
      font-size: bu(1.75);
      line-height: bu(2.5);
      padding-right: bu(1);
      padding-top: bu(2.5);
      padding: bu(2.5) bu(1) bu(1) 0;
    }

    &-image {
      margin-top: bu(3.25);
    }

    button {
      font-size: bu(1.875);
      margin-top: bu(1);
    }
  }

  &__type-edit {
    display: flex;
    flex-direction: column;
    padding-top: bu(1);
    width: 100%;

    h1 {
      color: map-get($foreground-color, light);
      font-size: bu(1.875);
      font-weight: 500;
      line-height: bu(2.5);
      padding-right: bu(6);
    }

    p, ul {
      font-size: bu(1.75);
      line-height: bu(2.5);
      padding: bu(0.5) bu(1) bu(1) 0;
    }

    &-actions {
      display: flex;
      justify-content: flex-end;
      padding-top: bu(3);

      button {
        margin: 0;
        min-width: bu(9.25);
      }
    }
  }

  &__calculation {
    display: flex;
    flex-direction: column;
    width: 100%;

    h1 {
      color: map-get($foreground-color, light);
      font-size: bu(1.875);
      font-weight: 500;
      padding-top: bu(3.25);
    }

    p {
      color: map-get($foreground-color, light);
      font-size: bu(1.75);
      line-height: bu(2.5);
      padding-right: bu(1);
      padding-top: bu(0.6);
    }

    &-image {
      margin-top: bu(3.25);
    }

    button {
      font-size: bu(1.875);
      margin-top: bu(2);
    }
  }

  &__deviation {
    padding: bu(2.5) 0 bu(1);
  }

  &__input-form-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding-top: bu(1);
  }

  &__input-measure {
    margin-top: bu(3.6);
    color: map-get($foreground-color, light);
    font-weight: 500;
    width: bu(9);
  }

  &__results {
    width: 100%;
    border-top: solid bu(0.125) map-get($foreground-color, darker);
    margin-top: bu(3);

    h1 {
      color: map-get($foreground-color, light);
      font-size: bu(1.875);
      font-weight: 500;
      line-height: bu(2);
      padding-top: bu(3);
    }

    p, ul {
      font-size: bu(1.65);
      line-height: bu(2.5);
      padding-right: bu(1);
      padding-top: bu(0.6);
    }

    &-rating {
      background-color: map-get($background-color, dark);
      border-radius: bu(0.75);
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: bu(1.5);
      height: bu(6);
      padding: bu(1.5);

      &--transparent {
        background-color: map-get($background-color, default);
        border-bottom: solid bu(0.15) map-get($foreground-color, darker);
        border-radius: 0;
        padding: bu(1.5) 0;
      }

      &-box {
        align-items: center;
        background-color: map-get($background-color, light);
        border-radius: bu(0.6);
        display: flex;
        height: bu(3);
        justify-content: space-between;
        padding: bu(1.25);
        width: bu(6.5);

        &--dark {
          background-color: map-get($background-color, dark);
          margin-top: bu(1);
        }
      }
    }

    &-current {
      border: bu(0.15) solid map-get($foreground-color, light);
      color: map-get($foreground-color, light);
      border-radius: bu(0.75);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-top: bu(3);
      padding: bu(1.25) bu(1.5);

      h1 {
        padding: 0;
      }
    }
  }

  &__det-mech-rating {
    @include set-font-family("condensed-tabular-numbers");
    align-items: center;
    color: map-get($foreground-color, light);
    display: flex;
    font-size: bu(1.875);
    font-weight: 700;
    margin-left: bu(1.5);
  }

  &__element-select {
    padding-top: bu(1);

    p {
      font-size: bu(1.75);
      font-weight: 400;
      line-height: bu(2.5);
      padding-top: bu(2);
      padding-bottom: bu(2);
    }
  }

  &__confirm-warning {
    display: flex;
    border: bu(0.15) solid map-get($foreground-color, light);
    border-radius: map-get($border-radius, heavy);
    color: map-get($foreground-color, light);
    margin-top: bu(1);
  }

  &__confirm-warning-icon {
    display: flex;
    align-items: flex-start;
    padding: bu(1.75) bu(1.75) 0 bu(2.5);
  }

  &__confirm-warning-message {
    padding: bu(1.6) bu(1.75) bu(2) 0;
  }
}

</style>
